// § 35a S5d: Bescheinigung für WEG-Eigentümer. Grundlage: vom Büro bestätigte Lohnanteile (rechnungen.lohnanteil_35a,
// Art aus rechnung_35a_vorschlaege) der im Jahr BEZAHLTEN Rechnungen der WEG. Verteilung nach MEA; Rechnungen mit
// Einheit (Sondereigentum) gehen ganz an diese Einheit. Ohne Zahlungsdatum oder ohne Art: nicht enthalten, nur Hinweis.
import { jsPDF } from 'jspdf';
import { supabase } from '../supabase/client';
import { LAYOUT, pdfText } from '../dokumente/blankoBrief';
import { teile, type WegEinheit } from './weg';

export interface Rechnung35aPosten {
  id: string; unternehmen: string | null; bezeichnung: string | null; bezahlt: string | null; lohn: number;
  art: 'handwerker' | 'haushaltsnah' | null; einheit_id: string | null;
}
export interface Anteil35a { datum: string; unternehmen: string; leistung: string; art: 'handwerker' | 'haushaltsnah'; gesamt: number; anteil: number }
export interface Bescheinigung35a {
  jahr: number; objekt: string; eigentuemer: string; einheiten: string; mea: number; meaGesamt: number;
  posten: Anteil35a[]; summeHaushaltsnah: number; summeHandwerker: number; hinweise: string[];
}

const r2 = (n: number) => Math.round(n * 100) / 100;

export function berechne35a(rechnungen: readonly Rechnung35aPosten[], einheiten: readonly WegEinheit[], eigentuemerId: string, jahr: number,
  kopf: { objekt: string; eigentuemer: string }): Bescheinigung35a {
  const meine = einheiten.filter((e) => e.eigentuemer_id === eigentuemerId);
  const hinweise: string[] = [];
  const ohneDatum = rechnungen.filter((r) => r.lohn > 0 && !r.bezahlt);
  if (ohneDatum.length) hinweise.push(`${ohneDatum.length} Rechnung(en) mit Lohnanteil ohne Zahlungsdatum – nicht enthalten.`);
  const ohneArt = rechnungen.filter((r) => r.lohn > 0 && r.bezahlt?.startsWith(String(jahr)) && !r.art);
  if (ohneArt.length) hinweise.push(`${ohneArt.length} Rechnung(en) ohne Art (Handwerker/haushaltsnah) – nicht enthalten.`);
  const posten: Anteil35a[] = [];
  for (const r of rechnungen) {
    if (!(r.lohn > 0) || !r.art || !r.bezahlt?.startsWith(String(jahr))) continue;
    const gewichte = r.einheit_id ? einheiten.map((e) => (e.einheit_id === r.einheit_id ? 1 : 0)) : einheiten.map((e) => e.mea ?? 0);
    const anteile = teile(r.lohn, gewichte);
    const anteil = r2(einheiten.reduce((s, e, i) => s + (e.eigentuemer_id === eigentuemerId ? anteile[i] : 0), 0));
    if (anteil === 0) continue;
    posten.push({ datum: r.bezahlt.slice(0, 10), unternehmen: r.unternehmen ?? '–', leistung: r.bezeichnung ?? '', art: r.art, gesamt: r.lohn, anteil });
  }
  posten.sort((a, b) => a.datum.localeCompare(b.datum));
  const summe = (art: Anteil35a['art']) => r2(posten.filter((p) => p.art === art).reduce((s, p) => s + p.anteil, 0));
  return {
    jahr, ...kopf, einheiten: meine.map((e) => e.bezeichnung).join(', '),
    mea: meine.reduce((s, e) => s + (e.mea ?? 0), 0), meaGesamt: einheiten.reduce((s, e) => s + (e.mea ?? 0), 0),
    posten, summeHaushaltsnah: summe('haushaltsnah'), summeHandwerker: summe('handwerker'), hinweise,
  };
}

/** Rechnungen der WEG mit Zahlung im Jahr und bestätigtem Lohnanteil (inkl. Art). */
export async function lade35aRechnungen(objektId: string, jahr: number): Promise<Rechnung35aPosten[]> {
  const { data, error } = await supabase.from('rechnungen')
    .select('id, unternehmen, bezeichnung, bezahlt_timestamp, lohnanteil_35a, einheit_id, datum')
    .eq('objekt_id', objektId).or('richtung.is.null,richtung.eq.eingang').is('archiviert_am', null).gt('lohnanteil_35a', 0)
    .or(`bezahlt_timestamp.is.null,and(bezahlt_timestamp.gte.${jahr}-01-01,bezahlt_timestamp.lt.${jahr + 1}-01-01)`);
  if (error) throw error;
  const zeilen = (data ?? []) as { id: string; unternehmen: string | null; bezeichnung: string | null; bezahlt_timestamp: string | null; lohnanteil_35a: number | string; einheit_id: string | null; datum: string | null }[];
  const ids = zeilen.map((z) => z.id);
  const arten = new Map<string, string>();
  if (ids.length) {
    const { data: v } = await supabase.from('rechnung_35a_vorschlaege').select('rechnung_id, art, status').in('rechnung_id', ids);
    for (const x of (v ?? []) as { rechnung_id: string; art: string; status: string }[]) if (x.status === 'bestaetigt') arten.set(x.rechnung_id, x.art);
  }
  return zeilen
    .filter((z) => z.bezahlt_timestamp || (z.datum ?? '').startsWith(String(jahr)))
    .map((z) => {
      const a = arten.get(z.id);
      return { id: z.id, unternehmen: z.unternehmen, bezeichnung: z.bezeichnung, bezahlt: z.bezahlt_timestamp, lohn: Number(z.lohnanteil_35a),
        art: a === 'handwerker' || a === 'haushaltsnah' ? a : null, einheit_id: z.einheit_id };
    });
}

const eurPdf = (n: number) => `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
const datum = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

export function erzeuge35aPdf(b: Bescheinigung35a, hintergrund: string | null, heute = new Date().toISOString().slice(0, 10)): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const L = LAYOUT; const li = L.links; const re = L.rechts;
  const papier = () => { if (hintergrund) pdf.addImage(hintergrund, 'JPEG', 0, 0, 210, 297, 'briefpapier', 'FAST'); };
  let y = 46;
  const t = (s: string, x: number, o?: { fett?: boolean; g?: number; grau?: boolean; rechts?: boolean }) => {
    pdf.setFont('helvetica', o?.fett ? 'bold' : 'normal'); pdf.setFontSize(o?.g ?? 10); const c = o?.grau ? 110 : 30; pdf.setTextColor(c, c, c);
    pdf.text(pdfText(s), x, y, o?.rechts ? { align: 'right' } : undefined);
  };
  const platz = (mm: number) => { if (y + mm > L.ende) { pdf.addPage(); papier(); y = L.folgeseiteStart; } };
  papier();
  t(`Bescheinigung über Aufwendungen nach § 35a EStG – ${b.jahr}`, li, { fett: true, g: 14 }); y += 7;
  t(`${b.objekt} · ${b.eigentuemer}`, li, { g: 11 }); y += 5.5;
  t(`Einheit(en): ${b.einheiten} · Miteigentumsanteil ${b.mea} / ${b.meaGesamt}`, li, { grau: true, g: 9 }); y += 9;
  const summe = (label: string, w: number) => { t(label, li, { fett: true }); t(eurPdf(w), re, { fett: true, rechts: true }); y += 6; };
  summe('Haushaltsnahe Dienstleistungen (§ 35a Abs. 2 EStG) – Ihr Anteil an den Arbeitskosten', b.summeHaushaltsnah);
  summe('Handwerkerleistungen (§ 35a Abs. 3 EStG) – Ihr Anteil an den Arbeitskosten', b.summeHandwerker);
  y += 4;
  t('Zugrunde liegende Rechnungen (Arbeits-, Maschinen- und Fahrtkosten inkl. USt., bezahlt im Jahr)', li, { fett: true, grau: true, g: 9 }); y += 5.5;
  t('Bezahlt', li, { grau: true, g: 8 }); t('Firma / Leistung', li + 22, { grau: true, g: 8 }); t('Art', 140, { grau: true, g: 8 }); t('gesamt', 165, { grau: true, g: 8, rechts: true }); t('Ihr Anteil', re, { grau: true, g: 8, rechts: true }); y += 5;
  for (const p of b.posten) {
    const zeilen = pdf.splitTextToSize(pdfText(`${p.unternehmen}${p.leistung ? ' – ' + p.leistung : ''}`), 112) as string[];
    platz(zeilen.length * 4.2 + 2);
    t(datum(p.datum), li, { g: 9 }); t(p.art === 'handwerker' ? 'Handwerker' : 'Haushaltsnah', 140, { g: 9 });
    t(eurPdf(p.gesamt), 165, { g: 9, rechts: true }); t(eurPdf(p.anteil), re, { g: 9, rechts: true });
    zeilen.forEach((z, i) => { if (i) y += 4.2; t(z, li + 22, { g: 9 }); });
    y += 5.2;
  }
  if (!b.posten.length) { t('Keine begünstigten Aufwendungen im Jahr.', li, { grau: true }); y += 6; }
  y += 4; platz(30);
  for (const h of [
    'Verteilung nach Miteigentumsanteilen; Aufwendungen für das Sondereigentum ganz an die betroffene Einheit.',
    'Die Steuerermäßigung können Sie nur für eine selbst genutzte Wohnung in Anspruch nehmen. Bei vermieteten Einheiten sind die Kosten Werbungskosten.',
    'Nicht begünstigt sind Material und Waren. Barzahlungen werden nicht berücksichtigt.',
    ...b.hinweise,
    'Zusammenstellung aus der Buchhaltung, keine Steuerberatung.',
  ]) { for (const z of pdf.splitTextToSize(pdfText(h), re - li) as string[]) { platz(5); t(z, li, { grau: true, g: 9 }); y += 4.5; } y += 1; }
  const n = pdf.getNumberOfPages();
  for (let s = 1; s <= n; s++) { pdf.setPage(s); pdf.setFontSize(8); pdf.setTextColor(110, 110, 110); pdf.text(pdfText(`Erstellt ${datum(heute)} · Immobilienmanagement Schwede`), li, 270); if (n > 1) pdf.text(`Seite ${s} von ${n}`, re, 270, { align: 'right' }); }
  return pdf;
}

export const dateiname35a = (b: Pick<Bescheinigung35a, 'jahr' | 'eigentuemer' | 'objekt'>) =>
  `35a-Bescheinigung ${b.jahr} ${b.objekt} ${b.eigentuemer}`.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() + '.pdf';
